import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

interface Product {
  id: string;
  name: string;
  description: string | null;
  unitPrice: number;
  currency: string;
  type: string;
  incomeLedgerAccountId?: string | null;
}

interface EditProductDialogProps {
  organisationId: string;
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditProductDialog({
  organisationId,
  product,
  open,
  onOpenChange,
  onUpdated,
}: EditProductDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [currency, setCurrency] = useState("");
  const [type, setType] = useState<"Product" | "Service">("Product");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (product) {
      setName(product.name ?? "");
      setDescription(product.description ?? "");
      setUnitPrice(String(product.unitPrice ?? ""));
      setCurrency(product.currency ?? "");
      setType((product.type as "Product" | "Service") ?? "Product");
      setError(null);
    }
  }, [product]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSubmitting(false);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!product) return;
    setError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const priceNum = Number(unitPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      setError("Unit price must be greater than zero.");
      return;
    }
    if (!currency.trim()) {
      setError("Please select a currency.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.PUT("/v1/products/{id}", {
      params: { path: { id: product.id } },
      body: {
        name: name.trim(),
        description: description.trim() || null,
        unitPrice: priceNum,
        currency,
        type,
        // PUT replaces every field: carry the product's income account through, or saving here would clear it.
        incomeLedgerAccountId: product.incomeLedgerAccountId ?? null,
      },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (apiError) {
      setError("Failed to update product.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onUpdated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>Update product details.</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-product-name">Name</Label>
              <Input
                id="edit-product-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-product-description">Description</Label>
              <Input
                id="edit-product-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-product-price">Unit Price</Label>
                <Input
                  id="edit-product-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-product-currency">Currency</Label>
                <CurrencyPicker
                  id="edit-product-currency"
                  value={currency}
                  onChange={setCurrency}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as "Product" | "Service")}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Product">Product</SelectItem>
                  <SelectItem value="Service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
